/**
 * FRONT-2036: extracted out of `message-bubble.tsx` so the phone surface
 * (`pwa-message.tsx`) can detect a sourced-briefing message without dragging
 * in that module's whole dependency tree — same dependency-free-pure-logic
 * precedent as `plan-status.ts`/`image-source.ts` (FRONT-1412/1423): no
 * React, no icons, no query hooks. `message-bubble.tsx` imports from here
 * now rather than defining its own copy, so the two surfaces can't drift on
 * what counts as a sourced briefing.
 */

/**
 * Detect a sourced briefing message produced by the skill runner. The
 * runner prefixes briefings with `[Skill: <slug>]` and (when a research
 * run row exists) a `[run: N]` marker line so the UI can attach a
 * Sources block on reload — chat_messages has no generic metadata
 * column, so we round-trip through the body text.
 *
 * Returns the stripped text (markers removed) plus the runId and the
 * skill's own slug, or null when the message isn't a sourced briefing. Any
 * grounded research/news skill that recorded a run (BUG-026) shows its
 * Sources block here, not just deep-research. The runId is null only for
 * legacy deep-research runs that predate the marker — the bubble still
 * renders, just without the Sources block. `skillSlug` (FRONT-2036 fix
 * round 1 item 12) is the raw `[Skill: <slug>]` value — the phone's
 * briefing card uses it to build a standalone fallback title ("Deep
 * research", "News") for a briefing whose body has no usable heading of
 * its own yet, rather than a generic sentence that repeats the card's own
 * "Research briefing" label.
 */
export function extractResearchBriefing(
  text: string,
): { stripped: string; runId: number | null; skillSlug: string } | null {
  const lines = text.split('\n');
  const skillMatch = lines[0]?.match(/^\[Skill:\s*([^\]]+)\]\s*$/);
  if (!skillMatch) return null;
  const slug = skillMatch[1].trim();

  // Drop the [Skill: …] line; tolerate an optional [run: N] line and an
  // optional blank separator before the briefing body.
  let i = 1;
  let runId: number | null = null;
  if (lines[i]) {
    const m = lines[i].match(/^\[run:\s*(\d+)\]\s*$/);
    if (m) {
      runId = parseInt(m[1], 10);
      i += 1;
    }
  }

  // deep-research strips its label even without a run marker (legacy).
  // Every OTHER skill is treated as a sourced briefing — label stripped,
  // Sources block attached — only when a [run: N] marker is present, so
  // ordinary skill-result messages are left untouched.
  if (slug !== 'deep-research' && runId === null) return null;

  if (lines[i] === '') i += 1;
  return { stripped: lines.slice(i).join('\n'), runId, skillSlug: slug };
}
